import { Typography } from "@mui/material";
import { CategoriesSlider } from "./contentList";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { useCallback, useEffect, useRef } from "react";
import { BackBarButtonMini } from "../common/backBarButton";
import smoothScroll from "../../utils/animateScroll";
import { useCategoryContentList } from "../../utils/hooks/useCategory";
import { useInputMode } from "../../utils/hooks/useInputMode";

const ContentWrapper = styled.div`
  flex: 1;
  overflow: hidden;
  height: 100vh;
  padding: 2rem 4rem;
  padding-bottom: 0;
`;

const ScrollingRows = styled.div`
  overflow-y: scroll;
  overflow-x: hidden;
  width: 100%;
  position: relative;
  height: calc(100vh - 64px);
  padding-bottom: 10vh;
`;

const Sentinel = styled.div`
  height: 1px;
`;

const MoftabakhContent = ({ data }) => {
  const { ref, focusKey } = useFocusable();
  const sentinelRef = useRef();

  const { items, hasMore, onLoadMore, isLoading } = useCategoryContentList({
    fetchDelay: 0,
    slug: "free",
    initialPage: 1,
    pageSize: 20,
    autoInit: false,
    initialData: data,
    lazyLoading: true,
  });

  const { isMouseFocus } = useInputMode();
  const onRowFocus = useCallback(
    (props) => {
      if (isMouseFocus) return;
      const { y, height } = props;
      const targetScroll = y > height - 200 ? y - 200 : 0;
      smoothScroll(ref, targetScroll, 500);
    },
    [ref, isMouseFocus]
  );

  // Primary mechanism: IntersectionObserver with lower threshold for reliability
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      { root: ref.current, threshold: 0.1 } // Lower threshold for better trigger reliability
    );

    const sentinel = sentinelRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasMore, isLoading, onLoadMore, ref]);

  // Backup mechanism: scroll position check for when IntersectionObserver fails
  useEffect(() => {
    const scrollContainer = ref.current;
    if (!scrollContainer) return;

    let scrollTimeout = null;
    const handleScroll = () => {
      // Debounce scroll handler
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!hasMore || isLoading) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // Trigger when within 200px of bottom (generous buffer for TV)
        if (distanceFromBottom < 200) {
          onLoadMore();
        }
      }, 100);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, isLoading, onLoadMore, ref]);

  return (
    <ContentWrapper>
      <FocusContext.Provider value={focusKey}>
        <div className="flex items-center mb-5">
          <BackBarButtonMini />
          <Typography
            variant="h4"
            fontWeight={700}
            mb={0}
            mr={3}
            color={"white"}
          >
            مفته باخ
          </Typography>
        </div>
        <ScrollingRows ref={ref}>
          <div>
            {items.length > 0 ? (
              <CategoriesSlider
                rowTitle={""}
                movies={items}
                onFocus={onRowFocus}
              />
            ) : (
              <div className="bg-[#212121] p-5 rounded-xl text-lg">
                <Typography color={"white"} fontFamily={"AnjomanMaxFN-Regular"}>
                  محتوایی یافت نشد
                </Typography>
              </div>
            )}
            <Sentinel ref={sentinelRef} />
            {isLoading && (
              <div className="flex justify-center items-center h-14">
                <Typography color={"white"} fontFamily={"AnjomanMaxFN-Regular"}>در حال بارگذاری...</Typography>
              </div>
            )}
          </div>
        </ScrollingRows>
      </FocusContext.Provider>
    </ContentWrapper>
  );
};

export default MoftabakhContent;
